import type { SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import type { TableSidebarItem } from '../api/contracts'
import type { FolderNode } from '../models'

/** Each table's last open (epoch ms), keyed by table id; never-opened tables are absent. */
export type LastOpenedMap = ReadonlyMap<string, number>

export const SIDEBAR_TABLE_SORT_OPTIONS: readonly { value: SidebarTableSort; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'recency', label: 'Recency' },
  { value: 'custom', label: 'Custom' },
]

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

/** Tables sort by the name the sidebar shows. */
function compareTableNames(a: TableSidebarItem, b: TableSidebarItem): number {
  return nameCollator.compare(a.display_name_plural, b.display_name_plural)
}

export function lastOpenedMapFromTables(tables: readonly TableSidebarItem[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const table of tables) {
    if (!table.last_opened_at) continue
    const time = Date.parse(table.last_opened_at)
    if (Number.isFinite(time)) map.set(table.id, time)
  }
  return map
}

export function sameLastOpened(a: LastOpenedMap, b: LastOpenedMap): boolean {
  if (a.size !== b.size) return false
  for (const [id, time] of a) if (b.get(id) !== time) return false
  return true
}

/**
 * Orders one list of tables. Name is A–Z by shown name. Recency puts the most
 * recently opened first, then never-opened tables A–Z. Custom follows
 * `customOrder`, with tables it doesn't list yet at the end, A–Z.
 */
export function orderTables(
  tables: readonly TableSidebarItem[],
  sort: SidebarTableSort,
  { customOrder = [], lastOpened }: { customOrder?: readonly string[]; lastOpened: LastOpenedMap }
): TableSidebarItem[] {
  const sorted = [...tables]
  if (sort === 'recency') {
    return sorted.sort((a, b) => {
      const at = lastOpened.get(a.id)
      const bt = lastOpened.get(b.id)
      if (at !== undefined && bt !== undefined && at !== bt) return bt - at
      if (at !== undefined && bt === undefined) return -1
      if (at === undefined && bt !== undefined) return 1
      return compareTableNames(a, b)
    })
  }
  if (sort === 'custom') {
    const position = new Map(customOrder.map((id, index) => [id, index]))
    return sorted.sort((a, b) => {
      const ai = position.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const bi = position.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return ai !== bi ? ai - bi : compareTableNames(a, b)
    })
  }
  return sorted.sort(compareTableNames)
}

/**
 * Applies a non-Custom sort to the folder tree: folders A–Z at every level
 * (they are expanded, not opened, so Recency leaves them A–Z too), and each
 * folder's tables in the sort's order. Custom keeps the tree as arranged.
 */
export function orderFolderTree(
  nodes: readonly FolderNode[],
  sort: SidebarTableSort,
  tablesById: ReadonlyMap<string, TableSidebarItem>,
  lastOpened: LastOpenedMap
): FolderNode[] {
  if (sort === 'custom') return [...nodes]
  return [...nodes]
    .sort((a, b) => nameCollator.compare(a.name, b.name))
    .map((node) => {
      const tables = node.tables.flatMap((ref) => {
        const table = tablesById.get(ref.table_id)
        return table ? [table] : []
      })
      return {
        ...node,
        children: orderFolderTree(node.children, sort, tablesById, lastOpened),
        tables: orderTables(tables, sort, { lastOpened }).map((table, index) => ({
          table_id: table.id,
          sort_order: index,
        })),
      }
    })
}

export function normalizeSearch(query: string): string {
  return query.trim().toLocaleLowerCase()
}

export function tableMatchesSearch(table: TableSidebarItem, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true
  return (
    table.display_name_plural.toLocaleLowerCase().includes(normalizedQuery) ||
    table.display_name_singular.toLocaleLowerCase().includes(normalizedQuery)
  )
}

/**
 * Narrows the folder tree to the tables a menu shows (its hidden and favorites
 * filters): a folder keeps the tables shown and the subfolders still holding
 * one, and is dropped when left with nothing; a folder that held no tables to
 * begin with stays only when `keepEmpty`.
 */
export function filterFolderTreeByTable(
  nodes: readonly FolderNode[],
  shows: (tableId: string) => boolean,
  keepEmpty: boolean
): FolderNode[] {
  return nodes.flatMap((node) => {
    const children = filterFolderTreeByTable(node.children, shows, keepEmpty)
    const tables = node.tables.filter((ref) => shows(ref.table_id))
    if (children.length > 0 || tables.length > 0) return [{ ...node, children, tables }]
    return keepEmpty && holdsNoTables(node) ? [{ ...node, children, tables }] : []
  })
}

function holdsNoTables(node: FolderNode): boolean {
  return node.tables.length === 0 && node.children.every(holdsNoTables)
}

/**
 * Narrows the folder tree to a search: a folder whose name matches keeps all
 * of its contents; otherwise it keeps only matching tables and the subfolders
 * that hold them, and is dropped when nothing inside matches.
 */
export function filterFolderTree(
  nodes: readonly FolderNode[],
  tablesById: ReadonlyMap<string, TableSidebarItem>,
  normalizedQuery: string
): FolderNode[] {
  if (!normalizedQuery) return [...nodes]
  return nodes.flatMap((node) => {
    if (node.name.toLocaleLowerCase().includes(normalizedQuery)) return [node]
    const children = filterFolderTree(node.children, tablesById, normalizedQuery)
    const tables = node.tables.filter((ref) => {
      const table = tablesById.get(ref.table_id)
      return table ? tableMatchesSearch(table, normalizedQuery) : false
    })
    return children.length > 0 || tables.length > 0 ? [{ ...node, children, tables }] : []
  })
}
